// -----------------------------------------------------------------------------
// Audit trail metadata — labels, icons, colours, chip whitelisting and
// consecutive-event collapsing for the reviewer's lifecycle timeline.
//
// Pure helpers only. No React, no DOM. The panel consumes these to keep the
// rendering logic declarative and the visual decisions auditable in one place.
// -----------------------------------------------------------------------------

import {
  AlertTriangle,
  Eye,
  FileScan,
  HelpCircle,
  Inbox,
  Layers,
  PenLine,
  Route,
  ScanText,
  ShieldCheck,
  Sparkles,
  Tags,
  ThumbsDown,
  ThumbsUp,
  Webhook,
  XOctagon,
  type LucideIcon,
} from 'lucide-react';

import type { AuditTrailEntryResponse } from '../api';
import { bandStyle } from './classificationMeta';

// ---------- Family taxonomy ----------

export type AuditEventFamily =
  | 'lifecycle'
  | 'routing'
  | 'ocr'
  | 'preparation'
  | 'ai'
  | 'validation'
  | 'verdict'
  | 'manual'
  | 'webhook'
  | 'segmentation'
  | 'classification';

/** Spanish friendly labels for the canonical lifecycle event types. */
export const EVENT_LABEL: Record<string, string> = {
  document_segmented: 'Segmentación',
  classification_started: 'Clasificación iniciada',
  document_classified: 'Documento clasificado',
  classification_unresolved: 'Tipo no determinado',
  classification_corrected: 'Tipo corregido',
  job_submitted: 'Recibido',
  extraction_path_resolved: 'Ruta de extracción resuelta',
  di_resource_resolved: 'Recurso DI resuelto',
  preparation_started: 'Preparación iniciada',
  preparation_completed: 'Preparación completada',
  preparation_failed: 'Preparación falló',
  preprocessing_started: 'OCR iniciado',
  preprocessing_completed: 'OCR completado',
  ai_extraction_started: 'Extracción IA iniciada',
  ai_extraction_completed: 'Extracción IA completada',
  validation_completed: 'Validación',
  job_approved: 'Aprobado',
  job_review_required: 'Requiere revisión',
  job_rejected: 'Rechazado',
  job_failed: 'Falló',
  job_cancellation_requested: 'Cancelación pedida',
  job_cancelled: 'Cancelado',
  manual_correction: 'Corrección manual',
  revalidation_triggered: 'Revalidación',
  webhook_delivered: 'Webhook entregado',
  webhook_failed: 'Webhook falló',
  webhook_dropped: 'Webhook descartado',
};

export const EVENT_FAMILY: Record<string, AuditEventFamily> = {
  document_segmented: 'segmentation',
  classification_started: 'classification',
  document_classified: 'classification',
  classification_unresolved: 'classification',
  classification_corrected: 'classification',
  job_submitted: 'lifecycle',
  extraction_path_resolved: 'routing',
  di_resource_resolved: 'routing',
  preparation_started: 'preparation',
  preparation_completed: 'preparation',
  preparation_failed: 'preparation',
  preprocessing_started: 'ocr',
  preprocessing_completed: 'ocr',
  ai_extraction_started: 'ai',
  ai_extraction_completed: 'ai',
  validation_completed: 'validation',
  job_approved: 'verdict',
  job_review_required: 'verdict',
  job_rejected: 'verdict',
  job_failed: 'verdict',
  job_cancellation_requested: 'lifecycle',
  job_cancelled: 'lifecycle',
  manual_correction: 'manual',
  revalidation_triggered: 'validation',
  webhook_delivered: 'webhook',
  webhook_failed: 'webhook',
  webhook_dropped: 'webhook',
};

/** Default icon per family. Overridable for verdict variants via {@link getEventIcon}. */
export const FAMILY_ICON: Record<AuditEventFamily, LucideIcon> = {
  lifecycle: Inbox,
  routing: Route,
  ocr: ScanText,
  preparation: FileScan,
  ai: Sparkles,
  validation: ShieldCheck,
  verdict: ThumbsUp,
  manual: PenLine,
  webhook: Webhook,
  segmentation: Layers,
  classification: Tags,
};

export interface FamilyColor {
  /** Marker background (filled) / border (hollow). */
  marker: string;
  /** Outer ring tone for the marker container. */
  ring: string;
  /** Default chip tone for chips that don't override. */
  chip: string;
}

export const FAMILY_COLOR: Record<AuditEventFamily, FamilyColor> = {
  lifecycle: {
    marker: 'bg-slate-500 border-slate-500 text-white',
    ring: 'ring-slate-200',
    chip: 'bg-slate-100 text-slate-700',
  },
  routing: {
    marker: 'bg-orange-500 border-orange-500 text-white',
    ring: 'ring-orange-100',
    chip: 'bg-orange-50 text-orange-700 border border-orange-200',
  },
  ocr: {
    marker: 'bg-cyan-600 border-cyan-600 text-white',
    ring: 'ring-cyan-100',
    chip: 'bg-cyan-50 text-cyan-700 border border-cyan-200',
  },
  preparation: {
    marker: 'bg-indigo-600 border-indigo-600 text-white',
    ring: 'ring-indigo-100',
    chip: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
  },
  ai: {
    marker: 'bg-violet-600 border-violet-600 text-white',
    ring: 'ring-violet-100',
    chip: 'bg-violet-50 text-violet-700 border border-violet-200',
  },
  validation: {
    marker: 'bg-emerald-600 border-emerald-600 text-white',
    ring: 'ring-emerald-100',
    chip: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  },
  verdict: {
    marker: 'bg-emerald-600 border-emerald-600 text-white',
    ring: 'ring-emerald-100',
    chip: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  },
  manual: {
    marker: 'bg-amber-500 border-amber-500 text-white',
    ring: 'ring-amber-100',
    chip: 'bg-amber-50 text-amber-700 border border-amber-200',
  },
  webhook: {
    marker: 'bg-fuchsia-600 border-fuchsia-600 text-white',
    ring: 'ring-fuchsia-100',
    chip: 'bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200',
  },
  segmentation: {
    marker: 'bg-teal-600 border-teal-600 text-white',
    ring: 'ring-teal-100',
    chip: 'bg-teal-50 text-teal-700 border border-teal-200',
  },
  classification: {
    marker: 'bg-sky-600 border-sky-600 text-white',
    ring: 'ring-sky-100',
    chip: 'bg-sky-50 text-sky-700 border border-sky-200',
  },
};

/**
 * Returns the icon for an event. For verdict events we pick a glyph per outcome;
 * for cancellations we swap to `XOctagon`. Falls back to the family default.
 */
export function getEventIcon(eventType: string): LucideIcon {
  switch (eventType) {
    case 'job_approved':
      return ThumbsUp;
    case 'job_review_required':
      return Eye;
    case 'job_rejected':
      return ThumbsDown;
    case 'job_failed':
      return AlertTriangle;
    case 'job_cancellation_requested':
    case 'job_cancelled':
      return XOctagon;
    case 'classification_unresolved':
      return HelpCircle;
    case 'classification_corrected':
      return PenLine;
    default: {
      const family = EVENT_FAMILY[eventType] ?? 'lifecycle';
      return FAMILY_ICON[family];
    }
  }
}

/** Whether a given event represents a "started" transition (hollow marker). */
export function isStartedEvent(eventType: string): boolean {
  return eventType.endsWith('_started') || eventType === 'job_cancellation_requested';
}

// ---------- Chip rendering ----------

export interface DetailChip {
  /** Visible text. */
  label: string;
  /**
   * Optional Tailwind utility group overriding the family default. Used for status-coloured chips
   * (validation status, route badges, failure reasons, …).
   */
  tone?: string;
}

const ROUTE_TONE: Record<string, string> = {
  R1: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  R2: 'bg-sky-100 text-sky-800 border border-sky-200',
  R3: 'bg-violet-100 text-violet-800 border border-violet-200',
  R4: 'bg-amber-100 text-amber-800 border border-amber-200',
  R5: 'bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-200',
  R6: 'bg-slate-100 text-slate-700 border border-slate-200',
};

const VALIDATION_TONE: Record<string, string> = {
  ok: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  warning: 'bg-amber-100 text-amber-800 border border-amber-200',
  warn: 'bg-amber-100 text-amber-800 border border-amber-200',
  critical: 'bg-rose-100 text-rose-800 border border-rose-200',
};

const BADGE_TONE = 'bg-slate-900 text-white';
const ROSE_TONE = 'bg-rose-50 text-rose-800 border border-rose-200';

/**
 * Compact numeric formatter — values ≥ 1000 collapse to a single decimal "k"
 * (e.g. 12340 → "12.3k"); smaller numbers render verbatim.
 */
function formatCompact(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return raw;
  }
  if (Math.abs(n) >= 1000) {
    return `${(n / 1000).toFixed(1)}k`;
  }
  return String(n);
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

/**
 * Returns the chip row to render under an entry. Whitelist-driven per event so that
 * unknown / not-listed keys are dropped silently and the UI stays deterministic.
 *
 * Detail key reference (non-exhaustive — see `AuditDetailKeys.cs` for the full list):
 *   preprocessing_started / preprocessing_completed: engineLabel, pageCount, textChars, ocrModelId
 *   ai_extraction_started / ai_extraction_completed:  providerType, providerScope, modelName,
 *     analyzerId (CU), inputTokens, outputTokens, fieldCount (CU)
 *   … (see individual cases below)
 */
export function getDetailChips(
  eventType: string,
  details: Record<string, string> | null | undefined,
): DetailChip[] {
  if (details === null || details === undefined) {
    return [];
  }
  const chips: DetailChip[] = [];

  const push = (label: string | null, tone?: string): void => {
    if (label !== null && label !== '') {
      chips.push(tone !== undefined ? { label, tone } : { label });
    }
  };

  switch (eventType) {
    case 'preprocessing_started':
    case 'preprocessing_completed': {
      const engine = details.engineLabel;
      if (engine !== undefined && engine !== '') {
        push(engine, BADGE_TONE);
      }
      // Why the OCR engine is what it is: a configured provider (e.g. Mistral) was bypassed at
      // resolution (license/limit/inactive) or failed at runtime, so the job ran on Document
      // Intelligence. Shown in amber so the reason is never buried — it answers "why DI, not Mistral?".
      if (details.ocrFallbackReason !== undefined && details.ocrFallbackReason !== '') {
        push(`fallback: ${details.ocrFallbackReason}`, 'bg-amber-50 text-amber-700 border border-amber-200');
      }
      if (details.pageCount !== undefined) {
        push(`${details.pageCount} págs`);
      }
      if (eventType === 'preprocessing_completed' && details.textChars !== undefined) {
        push(`${formatCompact(details.textChars)} chars`);
      }
      // ocrModelId is present for documents processed after Workstream B shipped.
      if (eventType === 'preprocessing_completed' && details.ocrModelId !== undefined && details.ocrModelId !== '') {
        push(details.ocrModelId, 'bg-cyan-50 text-cyan-700 border border-cyan-200');
      }
      break;
    }
    case 'ai_extraction_started':
    case 'ai_extraction_completed': {
      const providerType = details.providerType ?? '';
      if (providerType !== '') {
        push(providerType, BADGE_TONE);
      }
      if (details.providerScope !== undefined && details.providerScope !== '') {
        push(details.providerScope);
      }
      const isCu = providerType.toLowerCase().includes('contentunderstanding');
      if (isCu) {
        if (details.analyzerId !== undefined) {
          push(details.analyzerId);
        }
      } else if (details.modelName !== undefined) {
        push(details.modelName);
      }
      if (eventType === 'ai_extraction_completed') {
        if (isCu) {
          if (details.fieldCount !== undefined) {
            push(`${details.fieldCount} campos`);
          }
        } else {
          if (details.inputTokens !== undefined) {
            push(`${formatCompact(details.inputTokens)} tok in`);
          }
          if (details.outputTokens !== undefined) {
            push(`${formatCompact(details.outputTokens)} out`);
          }
        }
      }
      break;
    }
    case 'validation_completed': {
      if (details.violationsCount !== undefined) {
        push(`${details.violationsCount} violaciones`);
      }
      if (details.warningsCount !== undefined) {
        push(`${details.warningsCount} avisos`);
      }
      if (details.validationStatus !== undefined && details.validationStatus !== '') {
        const tone = VALIDATION_TONE[details.validationStatus.toLowerCase()];
        push(details.validationStatus, tone);
      }
      break;
    }
    case 'preparation_completed': {
      const route = details.finalRoute;
      if (route !== undefined && route !== '') {
        push(route, ROUTE_TONE[route] ?? BADGE_TONE);
      }
      if (details.attemptCount !== undefined) {
        push(`${details.attemptCount} intentos`);
      }
      if (details.estimatedCostUsd !== undefined) {
        const n = Number(details.estimatedCostUsd);
        push(Number.isFinite(n) ? `$${n.toFixed(3)}` : `$${details.estimatedCostUsd}`);
      }
      if (details.markdownChars !== undefined) {
        push(`${formatCompact(details.markdownChars)} md`);
      }
      if (details.candidateFieldCount !== undefined) {
        push(`${details.candidateFieldCount} candidatos`);
      }
      break;
    }
    case 'preparation_failed': {
      if (details.failureReason !== undefined && details.failureReason !== '') {
        push(truncate(details.failureReason, 80), ROSE_TONE);
      }
      break;
    }
    case 'job_approved':
    case 'job_review_required':
    case 'job_rejected':
    case 'job_failed': {
      if (details.globalScore !== undefined) {
        const n = Number(details.globalScore);
        push(Number.isFinite(n) ? `score ${n.toFixed(2)}` : `score ${details.globalScore}`);
      }
      if (details.triggerReason !== undefined && details.triggerReason !== '') {
        push(details.triggerReason);
      }
      break;
    }
    case 'extraction_path_resolved': {
      if (details.chosenRoute !== undefined && details.chosenRoute !== '') {
        push(details.chosenRoute, BADGE_TONE);
      }
      if (details.providerType !== undefined && details.providerType !== '') {
        push(details.providerType);
      }
      break;
    }
    case 'di_resource_resolved': {
      if (details.failureReason !== undefined && details.failureReason !== '') {
        push(truncate(details.failureReason, 80), ROSE_TONE);
      }
      if (details.diResourceId !== undefined && details.diResourceId !== '') {
        push(details.diResourceId, BADGE_TONE);
      }
      if (details.licenseType !== undefined && details.licenseType !== '') {
        push(details.licenseType);
      }
      break;
    }
    case 'manual_correction': {
      if (details.fieldName !== undefined && details.fieldName !== '') {
        push(details.fieldName);
      }
      break;
    }
    case 'webhook_delivered':
    case 'webhook_failed': {
      if (details.webhookUrlHost !== undefined && details.webhookUrlHost !== '') {
        push(truncate(details.webhookUrlHost, 40), BADGE_TONE);
      }
      if (details.httpStatusCode !== undefined && details.httpStatusCode !== '') {
        const tone = eventType === 'webhook_failed' ? ROSE_TONE : undefined;
        push(`HTTP ${details.httpStatusCode}`, tone);
      }
      if (
        eventType === 'webhook_failed' &&
        details.deliveryFailureReason !== undefined &&
        details.deliveryFailureReason !== ''
      ) {
        push(truncate(details.deliveryFailureReason, 80), ROSE_TONE);
      }
      if (details.deliveryAttempt !== undefined && details.deliveryAttempt !== '') {
        push(`intento ${details.deliveryAttempt}`);
      }
      break;
    }
    case 'document_classified': {
      // Resolved slug (dark badge) + confidence band (band-toned) + optional runner-up considered.
      if (details.documentTypeSlug !== undefined && details.documentTypeSlug !== '') {
        push(details.documentTypeSlug, BADGE_TONE);
      }
      if (details.confidenceBand !== undefined && details.confidenceBand !== '') {
        const style = bandStyle(details.confidenceBand);
        push(`confianza ${style.label}`, style.chip);
      }
      if (details.runnerUpSlug !== undefined && details.runnerUpSlug !== '') {
        push(`alt: ${details.runnerUpSlug}`);
      }
      break;
    }
    case 'classification_corrected': {
      // predicted → corrected, plus the band the (wrong) auto-classification carried.
      const predicted = details.predictedSlug;
      const corrected = details.correctedSlug;
      if (predicted !== undefined && predicted !== '' && corrected !== undefined && corrected !== '') {
        push(`${predicted} → ${corrected}`, BADGE_TONE);
      } else if (corrected !== undefined && corrected !== '') {
        push(corrected, BADGE_TONE);
      }
      if (details.confidenceBand !== undefined && details.confidenceBand !== '') {
        const style = bandStyle(details.confidenceBand);
        push(`era ${style.label}`, style.chip);
      }
      break;
    }
    case 'classification_unresolved': {
      if (details.reason !== undefined && details.reason !== '') {
        push(details.reason, ROSE_TONE);
      }
      break;
    }
    case 'document_segmented': {
      // Strategy badge — always present.
      if (details.segmentationStrategy !== undefined && details.segmentationStrategy !== '') {
        push(details.segmentationStrategy, BADGE_TONE);
      }
      // Segment count.
      if (details.segmentCount !== undefined && details.segmentCount !== '') {
        push(`${details.segmentCount} segmentos`);
      }
      // Reason — rose/danger tone for degraded paths; neutral otherwise.
      if (details.segmentationReason !== undefined && details.segmentationReason !== '') {
        const degradedReasons = new Set([
          'identifier_pattern_empty',
          'identifier_no_match',
          'identifier_invalid_regex',
          'ai_oversplit_capped',
          'ai_no_provider',
        ]);
        const reasonTone = degradedReasons.has(details.segmentationReason) ? ROSE_TONE : undefined;
        push(details.segmentationReason, reasonTone);
      }
      // Model name — only present on the AI path.
      if (details.modelName !== undefined && details.modelName !== '') {
        push(details.modelName);
      }
      break;
    }
    default:
      break;
  }

  return chips;
}

// ---------- Consecutive collapsing ----------

export interface CollapsedEntry {
  eventType: string;
  actorId: string;
  firstAt: string;
  lastAt: string;
  occurrences: number;
  /** Last entry's durationMs (most representative for "completed" duplicates). */
  durationMs: number | null;
  /** Detail map taken from the last entry of the group. */
  details: Record<string, string> | null;
  /** Chip set computed on the representative entry; reused as the equality key. */
  chips: DetailChip[];
}

function chipKey(chips: DetailChip[]): string {
  // Tone is rendering-only — equality should be based on label set + order.
  return chips.map((c) => c.label).join('|');
}

/**
 * Groups consecutive entries that share the same `eventType` AND the same chip set (label-wise)
 * into a single rendered row. The last entry of each group is taken as the representative so
 * the most up-to-date duration / details surface.
 */
export function collapseConsecutive(
  entries: ReadonlyArray<AuditTrailEntryResponse>,
): CollapsedEntry[] {
  const result: CollapsedEntry[] = [];
  for (const entry of entries) {
    const chips = getDetailChips(entry.eventType, entry.details ?? null);
    const last = result.length > 0 ? result[result.length - 1] : undefined;
    if (
      last !== undefined &&
      last.eventType === entry.eventType &&
      chipKey(last.chips) === chipKey(chips)
    ) {
      last.lastAt = entry.at;
      last.occurrences += 1;
      last.durationMs = entry.durationMs ?? last.durationMs;
      last.details = entry.details ?? last.details;
      last.actorId = entry.actorId;
      // chips remain identical by definition.
    } else {
      result.push({
        eventType: entry.eventType,
        actorId: entry.actorId,
        firstAt: entry.at,
        lastAt: entry.at,
        occurrences: 1,
        durationMs: entry.durationMs ?? null,
        details: entry.details ?? null,
        chips,
      });
    }
  }
  return result;
}
